import { type RefObject, useCallback, useEffect, useState } from 'react';

import { BannerSection } from '../components/banner-section';

type TpUsePaymentSectionScrollProps = {
  refs: RefObject<HTMLElement>[];
  onDiscountTimeFinished?: () => void;
  isNgrow?: boolean;
};

const SCROLL_THRESHOLD = 500;

export const usePaymentSectionScroll = ({
  refs,
  onDiscountTimeFinished,
  isNgrow = false,
}: TpUsePaymentSectionScrollProps) => {
  const [currentSectionRef, setCurrentSectionRef] = useState(refs[0]);
  const [isHideHeader, setHideHeader] = useState(false);
  const [isHideFooter, setHideFooter] = useState(false);
  const [initFooter, setFooter] = useState(false);

  const scrollToPaymentSection = useCallback(
    (sectionRef: RefObject<HTMLElement>) => {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    },
    [],
  );

  const getVisibleSectionIndex = useCallback(
    (sectionRects: DOMRect[]) =>
      sectionRects.findIndex(
        (rect) => rect.top < window.innerHeight && rect.bottom > 0,
      ),
    [],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: getVisibleSectionIndex
  const handleScroll = useCallback(() => {
    if (refs.every((ref) => ref.current)) {
      const sectionRects = refs.map((ref) =>
        ref.current!.getBoundingClientRect(),
      );
      const visibleSectionIndex = getVisibleSectionIndex(sectionRects);

      if (visibleSectionIndex !== -1) {
        setCurrentSectionRef(refs[visibleSectionIndex]);
      }

      const firstSectionRect = sectionRects[0];
      setFooter(firstSectionRect.top <= 0);
      setHideHeader(firstSectionRect.bottom <= SCROLL_THRESHOLD);
      setHideFooter(firstSectionRect.bottom >= SCROLL_THRESHOLD);
    }
  }, [refs]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const renderHeaderSection = useCallback(
    () => (
      <BannerSection
        isHeader={true}
        isHide={isHideHeader}
        onClick={() => scrollToPaymentSection(currentSectionRef)}
        onFinish={onDiscountTimeFinished}
        isNgrow={isNgrow}
      />
    ),
    [
      isHideHeader,
      scrollToPaymentSection,
      currentSectionRef,
      onDiscountTimeFinished,
      isNgrow,
    ],
  );

  const renderFooterSection = useCallback(
    () => (
      <BannerSection
        isHeader={false}
        isHide={isHideFooter}
        initFooter={initFooter}
        onClick={() => scrollToPaymentSection(currentSectionRef)}
        onFinish={onDiscountTimeFinished}
        isNgrow={isNgrow}
      />
    ),
    [
      isHideFooter,
      initFooter,
      currentSectionRef,
      scrollToPaymentSection,
      onDiscountTimeFinished,
      isNgrow,
    ],
  );

  return {
    currentSectionRef,
    scrollToPaymentSection,
    renderHeaderSection,
    renderFooterSection,
  };
};
